import { observer } from 'mobx-react-lite'
import { useJobDetailsViewModel } from '@/features/jobs/details/JobDetailsView'
import { useActivation } from '@/hooks/view-model-hooks'
import type { DefaultJobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { TaxJobMessagesTab } from '@/features/jobs/details/tax-type/components/TaxJobMessagesTab'
import { DefaultJobConversationContainer } from '@/features/jobs/details/components/DefaultJobConversationContainer'
import { MessagesTabLayout } from '@/features/jobs/details/components/MessagesTabLayout'
import { useHideGlobalNavbar } from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useProvideSupportWidgetHidden } from '@/features/support/components/ChatSupportSpaceManager'
import { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import {
  ScheduleCallCalloutContainer,
  ScheduleCallLeadingActionContainer,
} from '@/features/jobs/details/consultation/components/ScheduleCallComposerAction'

/**
 * Job messages tab.
 */
export default observer(function MessagesTab() {
  const vm = useJobDetailsViewModel()
  useActivation(vm.conversationVm)

  // Hide the navbar on smaller screens.
  const isSpacious = useIsSpacious()
  useHideGlobalNavbar(!isSpacious)

  // Hide support chat when viewing messages.
  useProvideSupportWidgetHidden(true)

  if (vm.typeVm instanceof TaxJobDetailsViewModel) {
    return <TaxJobMessagesTab taxVm={vm.typeVm} />
  }

  if (vm.typeVm instanceof ConsultationJobDetailsViewModel) {
    return (
      <ConsultationJobMessagesTab
        vm={vm.typeVm.jobConversationVm}
        consultationVm={vm.typeVm}
      />
    )
  }

  return <DefaultJobMessagesTab vm={vm.typeVm.jobConversationVm} />
})

interface DefaultJobMessagesTabProps {
  vm: DefaultJobConversationViewModel
}

const DefaultJobMessagesTab = observer(function DefaultJobMessagesTab({
  vm,
}: DefaultJobMessagesTabProps) {
  return (
    <MessagesTabLayout>
      <DefaultJobConversationContainer
        scrollingElement={document.documentElement}
        vm={vm}
        composerPositioning="fixed"
      />
    </MessagesTabLayout>
  )
})

interface ConsultationJobMessagesTabProps {
  vm: DefaultJobConversationViewModel
  consultationVm: ConsultationJobDetailsViewModel
}

const ConsultationJobMessagesTab = observer(
  function ConsultationJobMessagesTab({
    vm,
    consultationVm,
  }: ConsultationJobMessagesTabProps) {
    return (
      <MessagesTabLayout>
        <DefaultJobConversationContainer
          scrollingElement={document.documentElement}
          vm={vm}
          composerPositioning="fixed"
          composerCallout={
            <ScheduleCallCalloutContainer
              consultationManagerVm={consultationVm.consultationManagerVm}
            />
          }
          composerLeadingActions={
            <ScheduleCallLeadingActionContainer
              consultationManagerVm={consultationVm.consultationManagerVm}
            />
          }
        />
      </MessagesTabLayout>
    )
  },
)
